import { ReactNode, ComponentType } from "react";
import { LucideIcon } from "lucide-react";
import { Card, CardContent } from "../ui/card";
import { cn } from "../../utils/utils";

interface EmptyStateCardProps {
    icon: LucideIcon | ComponentType<{ className?: string }>;
    title: string;
    description?: string;
    action?: ReactNode;
    className?: string;
    compact?: boolean;
    /** Render without an outer card — use inside tables or existing cards */
    embedded?: boolean;
}

function EmptyStateContent({
    icon: Icon,
    title,
    description,
    action,
    compact,
}: Omit<EmptyStateCardProps, "className" | "embedded">) {
    return (
        <>
            <div
                className={cn(
                    "rounded-full bg-muted flex items-center justify-center mb-4",
                    compact ? "w-12 h-12" : "w-16 h-16",
                )}
            >
                <Icon className={cn("text-muted-foreground", compact ? "w-6 h-6" : "w-8 h-8")} />
            </div>
            <h3 className="text-lg font-semibold mb-2">{title}</h3>
            {description ? (
                <p className="text-sm text-muted-foreground max-w-md">{description}</p>
            ) : null}
            {action ? <div className="mt-4">{action}</div> : null}
        </>
    );
}

export default function EmptyStateCard({
    icon,
    title,
    description,
    action,
    className,
    compact = false,
    embedded = false,
}: EmptyStateCardProps) {
    if (embedded) {
        return (
            <div
                className={cn(
                    "flex flex-col items-center justify-center text-center",
                    compact ? "py-8" : "py-12",
                    className,
                )}
            >
                <EmptyStateContent
                    icon={icon}
                    title={title}
                    description={description}
                    action={action}
                    compact={compact}
                />
            </div>
        );
    }

    return (
        <Card className={cn("border-dashed", className)}>
            <CardContent
                className={cn(
                    "flex flex-col items-center justify-center text-center",
                    compact ? "py-8" : "py-12",
                )}
            >
                <EmptyStateContent
                    icon={icon}
                    title={title}
                    description={description}
                    action={action}
                    compact={compact}
                />
            </CardContent>
        </Card>
    );
}
